import { Injectable } from '@angular/core';
import { isNil } from 'lodash';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, map, mergeMap, shareReplay, switchMap, tap, withLatestFrom, first } from 'rxjs/operators';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { PermissionCode } from '@app/shared/enums';
import { CustomerCenter, CustomerCenterService } from '@app/shared/services/api/customer-center.service';
import { DeleteTestDataService } from '@app/shared/services/api/delete-test-data.service';
import { MunicipalityRestriction, MunicipalityRestrictionsService } from '@app/shared/services/api/municipality-restrictions.service';
import { EditableMunicipalityConfig, Municipality, MunicipalityService } from '@app/shared/services/api/municipality.service';
import { Permission, PermissionService } from '@app/shared/services/api/permission.service';
import { MunicipalityRegionHierarchy, RegionMunicipalitiesService } from '@app/shared/services/api/region-municipalities.service';
import { Region, RegionService } from '@app/shared/services/api/regions.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { CustomerCenterConfig, MunicipalityStoreConfig, PermissionRequestPayload, RegionAssignment } from '../models/municipality-management';
import { MunicipalityManagementConfigurationStore } from './municipality-management-configuration.store';
import { WarmedatenPermissionService } from '@app/warmedaten/services/warmedaten-permission.service';
import { ExterneAppsFacadeService } from './externe-apps-facade.service';
import { MunicipalityManagementQueueStateService, PermissionType, RestrictionAction } from './municipality-management-queue-state.service';
import { PermissionStatusCode } from '../models/externe-apps';
import { MunicipalityManagementDialogHelperService } from './municipality-management-dialog-helper.service';

@Injectable({
  providedIn: 'root',
})
export class MunicipalityManagementService {
  private _isDmgReportTabEnabled: boolean = false;
  private _isSaving: boolean = false;

  constructor(
    private _userMunicipalitiesService: UserMunicipalitiesService,
    private _customerCenterService: CustomerCenterService,
    private _municipalityService: MunicipalityService,
    private _permissionService: PermissionService,
    private _municipalityRestrictionService: MunicipalityRestrictionsService,
    private _testDataService: DeleteTestDataService,
    private _regionService: RegionService,
    private _regionMunicipalitiesService: RegionMunicipalitiesService,
    private _configStore: MunicipalityManagementConfigurationStore,
    private _authService: AuthService,
    private _loader: OverlayLoaderService,
    private _warmedatenPermissionService: WarmedatenPermissionService,
    private _externeAppsFacadeService: ExterneAppsFacadeService,
    private _municipalityManagementQueueStateService: MunicipalityManagementQueueStateService,
    private readonly _municipalityManagementDialogHelperService: MunicipalityManagementDialogHelperService,
    private readonly _appInsightsService: AppInsightsServiceDirective
  ) {}

  public initializeConfigurationStore = (): Observable<MunicipalityStoreConfig> => {
    this._loader.isLoading = true;
    return this._userMunicipalitiesService.currentMunicipality$.pipe(
      filter((municipality) => !isNil(municipality)),
      mergeMap((municipality: MapStoreMunicipality) => {
        return forkJoin([
          this.getMunicipality(municipality.guid),
          this.getMunicipalityRestrictions(municipality.guid),
          this.getRegionMunicipality(municipality.guid),
          this.getAllCustomerCenters(),
          this.getPagePermission(),
          this.getRegions(),
        ]).pipe(finalize(() => (this._loader.isLoading = false)));
      }),
      map(([municipalityInfo, municipalityRestrictions, regionMunicipality, customerCenters, pagePermissions, regions]) => {
        const municipality: EditableMunicipalityConfig = {
          guid: municipalityInfo[0].guid,
          climateNaviUrl: municipalityInfo[0].climateNaviUrl,
          customerCenter: municipalityInfo[0].customerCenter,
          customerCenterId: municipalityInfo[0].customerCenterId,
          dmgRepPwaactive: municipalityInfo[0].dmgRepPwaactive,
          energyMonitorUrl: municipalityInfo[0].energyMonitorUrl,
          isActive: municipalityInfo[0].isActive,
          isDeactivated: municipalityInfo[0].isDeactivated,
          isGeneralReportingDamageEnabled: municipalityInfo[0].isGeneralReportingDamageEnabled,
          isInTestMode: municipalityInfo[0].isInTestMode,
          localElectricityMarketUrl: municipalityInfo[0].localElectricityMarketUrl,
          modifyUser: this._authService.currentUser.guid,
          municipalityKey: municipalityInfo[0].municipalityKey,
          name: municipalityInfo[0].name,
          roomMonitoringUrl: municipalityInfo[0].roomMonitoringUrl,
          zipCode: municipalityInfo[0].zipCode,
        };
        return {
          municipality: municipality,
          regionAssignment: this.municipalityRegionAssignment(regions, regionMunicipality),
          customerCenter: this.municipalityCustomerCenterConfig(customerCenters, municipality),
          pagePermissions: this.municipalityPagePermissionConfig(pagePermissions, municipalityRestrictions),
          isLoading: false,
          canNavigate: true,
        };
      }),
      tap((config: MunicipalityStoreConfig) => {
        this._configStore.oldRegionId = config.regionAssignment.regionId;
        this._configStore.initializeConfigState(config);
      })
    );
  };

  public get isDmgReportTabEnabled() {
    return this._isDmgReportTabEnabled;
  }

  private getMunicipality(guid: string): Observable<Municipality[]> {
    return this._municipalityService.getByMunicipalityGuid(guid).pipe(catchError(() => of([])));
  }

  private getMunicipalityRestrictions(guid: string): Observable<MunicipalityRestriction[]> {
    return this._municipalityRestrictionService.getByMunicpalityId(guid).pipe(catchError(() => of([])));
  }

  private getRegionMunicipality(guid: string) {
    return this._regionMunicipalitiesService.getMunicipalityRegionMappingByMunicipalityId(guid).pipe(catchError(() => of([])));
  }

  private getAllCustomerCenters(): Observable<CustomerCenter[]> {
    return this._customerCenterService.getAll().pipe(catchError(() => of([])));
  }

  private getPagePermission(): Observable<Permission[]> {
    return this._permissionService.getPagePermissons().pipe(catchError(() => of([])));
  }

  private getRegions(): Observable<Region[]> {
    return this._regionService.getRegionTypeByCode('AM').pipe(
      switchMap((regionTypes) => this._regionService.getByRegionTypeId(regionTypes[0].guid)),
      shareReplay(),
      catchError(() => of([]))
    );
  }

  private municipalityRegionAssignment(regions: Region[], regionMunicipality: MunicipalityRegionHierarchy[]): RegionAssignment {
    const assignedRegion: MunicipalityRegionHierarchy = regionMunicipality[0];
    const sortedRegions = [...regions].sort((a, b) => a.name.localeCompare(b.name));
    return {
      data: sortedRegions,
      selected: assignedRegion.adminRegionName,
      regionId: assignedRegion.adminRegionId,
      oldRegionId: assignedRegion.adminRegionId,
    } as RegionAssignment;
  }

  private municipalityPagePermissionConfig(permissions: Permission[], restrictions: MunicipalityRestriction[]) {
    const damageReportCodes = [PermissionCode.SOCKET_SERVICE, PermissionCode.STREETLIGHT_PLANNER].map((code: PermissionCode) => code.toString());
    this._isDmgReportTabEnabled = permissions.some((permission: Permission) => damageReportCodes.includes(permission.code) && !!permission.isActive);

    return permissions.map((pagePermission: Permission) => {
      const restriction = restrictions.find((municipalityRestriction: MunicipalityRestriction) => municipalityRestriction.permissionId === pagePermission.guid);

      pagePermission.isRestricted = !isNil(restriction);
      pagePermission.isTeaser = restriction?.isTeaser ?? false;

      return pagePermission;
    });
  }

  private municipalityCustomerCenterConfig(customerCenters: CustomerCenter[], municipality: EditableMunicipalityConfig): CustomerCenterConfig {
    const selectedCenter = customerCenters.find((e) => e.itemId === +municipality.customerCenter);

    return {
      data: customerCenters,
      selected: selectedCenter?.name ?? '',
      damageReportEmailAddress: selectedCenter?.damageReportEmailAddress ?? '',
    };
  }

  public resetChanges(): void {
    this._configStore.resetChangesToStore();
  }

  public saveChanges(navigateAway = false): Observable<boolean | unknown> {
    if (this._isSaving) {
      return of(false);
    }

    let confirmChanges: Observable<number>;
    if (this._configStore.isFormValid) {
      confirmChanges = this._municipalityManagementDialogHelperService.saveConfirmation();
    } else {
      confirmChanges = this._municipalityManagementDialogHelperService.saveConfirmationError();
    }

    return confirmChanges.pipe(
      switchMap((result: number) => {
        if (navigateAway) {
          this._configStore.clear();
        }
        switch (result) {
          case 0:
            if (navigateAway) {
              this._externeAppsFacadeService.clearQueueStateService();
              if (this._configStore.updatedExterneUrl) {
                window.location.reload();
                this._configStore.updatedExterneUrl = false;
              }
            }
            return of(true);
          case 1:
            return this.saveMunicipalityConfiguration();
          case 2:
          default:
            return of(false);
        }
      })
    );
  }

  public formValidation(): Observable<boolean> {
    return this._configStore.isFormValid$;
  }

  public openDeleteTestDataDialog(): Observable<boolean> {
    return of(this._configStore.municipalityConfig.isInTestMode).pipe(
      switchMap((isInTestMode: boolean) =>
        isInTestMode
          ? this._municipalityManagementDialogHelperService.testDataConfirmation().pipe(
              switchMap((confirmChanges) => (confirmChanges ? this.deleteTestData() : of(true))),
              finalize(() => (this._loader.isLoading = false))
            )
          : this._municipalityManagementDialogHelperService.testDataErrorNotTestMunicipality()
      )
    );
  }

  private saveMunicipalityConfiguration(): Observable<boolean> {
    this._loader.isLoading = true;
    this._isSaving = true;

    return this.updateRequests().pipe(
      tap(() => this._externeAppsFacadeService.savingError$.next(false)),
      mergeMap(() => this.postUpdate()),
      withLatestFrom(this._externeAppsFacadeService.savingError$),
      switchMap(([savedResponse, errors]) => {
        if (errors) {
          this._municipalityManagementDialogHelperService.saveError();
          return throwError(() => 'Error');
        } else {
          this._municipalityManagementDialogHelperService.saveSuccess();
        }
        this._municipalityManagementQueueStateService.clearRestrictions();
        this._authService.applyMunicipalityRestrictions(this._userMunicipalitiesService.currentMunicipality.guid);
        return of(true);
      }),
      catchError(() => {
        this._municipalityManagementDialogHelperService.saveError();
        return of(false);
      }),
      finalize(() => {
        this._loader.setLoadingToFalse();
        this._isSaving = false;
      })
    );
  }

  private updateRequests(): Observable<any> {
    return this.updateRestrictions().pipe(
      mergeMap(() => {
        if (this._configStore.municipalityHasChanges()) {
          return this.updateMunicipality();
        } else {
          return of([]);
        }
      }),
      mergeMap(() => {
        if (this._configStore.regionHasChanges()) {
          const regionIds = this._configStore.municipalityRegionAssignment?.regionId;
          if (!!regionIds?.length) {
            return this.updateRegionAssignment();
          } else {
            return this.deleteOldRegionId();
          }
        } else {
          return of([]);
        }
      }),
      mergeMap(() => this._externeAppsFacadeService.applyIndividualLinks(this._configStore.municipalityConfig.guid)),
      catchError((error) => {
        this._externeAppsFacadeService.savingError$.next(true);
        return of([]);
      })
    );
  }

  private updateMunicipality(): Observable<any> {
    return this._municipalityService.updateMunicipality(this._configStore.municipalityConfig).pipe(
      first(),
      tap(() => this._userMunicipalitiesService.updateName(this._configStore.municipalityConfig))
    );
  }

  private updateRestrictions(): Observable<any[]> {
    const municipalityKey = this._userMunicipalitiesService.currentMunicipality.key;
    const municipalityId = this._userMunicipalitiesService.currentMunicipality.guid;

    return this._municipalityManagementQueueStateService.restrictions$.pipe(
      first(),
      tap((restrictions) => {
        if (
          restrictions.some(
            (restriction) =>
              restriction.featurePermission.code === PermissionCode.WARMEDATEN && restriction.statusCode === PermissionStatusCode.ACTIVATEDBYMUNICIPALITY
          )
        ) {
          this._warmedatenPermissionService
            .checkUserEmail(municipalityId)
            .pipe(
              first(),
              catchError(() => of(null))
            )
            .subscribe();
        }
        return of(null);
      }),
      mergeMap((restrictionsAll) => {
        if (restrictionsAll.length > 0) {
          let requests: PermissionRequestPayload[] = [];
          for (const restriction of restrictionsAll) {
            switch (restriction.statusCode) {
              case PermissionStatusCode.ACTIVATEDBYMUNICIPALITY:
                if (restriction.permissionType !== PermissionType.CUSTOM || !restriction.featurePermission.isGenericLink) {
                  requests.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.ACTIVATE });
                }
                break;
              case PermissionStatusCode.DEACTIVATEDBYMUNICIPALITY:
                requests.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.DEACTIVATE });
                break;
              case PermissionStatusCode.TEASERBYMUNICIPALITY:
                requests.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.TEASER });
                break;
            }
          }

          if (requests.length > 0) {
            return this._permissionService.assignRestrictions(requests, municipalityKey, this._authService.currentUser.guid).pipe(first());
          } else return of([]);
        } else return of([]);
      }),
      withLatestFrom(this._municipalityManagementQueueStateService.restrictions$),
      switchMap(([response, restrictionsAll]) => {
        if (restrictionsAll.length > 0) {
          let requestsAll: PermissionRequestPayload[] = [];
          for (const restriction of restrictionsAll) {
            switch (restriction.statusCode) {
              case PermissionStatusCode.ACTIVATEDBYMUNICIPALITY:
                if (restriction.permissionType === PermissionType.CUSTOM && restriction.featurePermission.isGenericLink) {
                  requestsAll.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.DEACTIVATE });
                }
                break;
              case PermissionStatusCode.ACTIVATEDALL:
                requestsAll.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.ACTIVATE });
                break;
              case PermissionStatusCode.TEASERALL:
                requestsAll.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.TEASER });
                break;
              case PermissionStatusCode.DEACTIVATEDALL:
                requestsAll.push({ permissionId: restriction.featurePermission.guid, action: RestrictionAction.DEACTIVATE });
                break;
            }
          }

          if (requestsAll.length > 0) {
            return this._permissionService.assignRestrictions(requestsAll, null, this._authService.currentUser.guid).pipe(first());
          } else return of([]);
        } else return of([]);
      })
    );
  }

  private postUpdate(): Observable<any> {
    this._configStore.setMunicipalityConfigState(this._configStore.municipalityStoreConfig);

    return this._municipalityManagementQueueStateService.restrictions$.pipe(
      first(),
      switchMap((restrictions) => {
        let requests = [];
        if (restrictions.length > 0) {
          const activatedByMunicipalityRestrictions = restrictions.filter(
            (restriction) =>
              restriction.permissionType === PermissionType.CUSTOM &&
              restriction.featurePermission.isGenericLink &&
              restriction.statusCode === PermissionStatusCode.ACTIVATEDBYMUNICIPALITY
          );
          if (activatedByMunicipalityRestrictions.length > 0) {
            requests.push(
              this._permissionService.assignRestrictions(
                activatedByMunicipalityRestrictions.map((restriction) => ({
                  permissionId: restriction.featurePermission.guid,
                  action: RestrictionAction.ACTIVATE,
                })),
                this._userMunicipalitiesService.currentMunicipality.key,
                this._authService.currentUser.guid
              )
            );
          }

          if (requests.length > 0) {
            return forkJoin(requests);
          } else return of([]);
        }
        return of([]);
      })
    );
  }

  private deleteOldRegionId(): Observable<any> {
    const municipalityId = this._configStore.municipalityConfig.guid;
    const requests: Observable<any>[] = this._configStore.oldRegionId.map((regionId: string) =>
      this._regionMunicipalitiesService.deleteRegionMunicipalityMapping(regionId, municipalityId)
    );
    return requests.length > 0 ? forkJoin(requests) : of(null);
  }

  private postMunicipalityRegionMapping(): Observable<any> {
    const { regionId } = this._configStore.municipalityRegionAssignment;
    const municipalityId = this._configStore.municipalityConfig.guid;

    if (regionId.length === 0) {
      this._appInsightsService.logError(new Error('No region to post - municipality left without a region mapping'), {
        context: 'municipality region assignment',
        municipalityGuid: municipalityId,
      });
      this._configStore.oldRegionId = [];
      return of(null);
    }

    return forkJoin(regionId.map((regionId) => this._regionMunicipalitiesService.postMunicipalityRegionMapping({ regionId, municipalityId }))).pipe(
      tap((responses) => {
        this._configStore.oldRegionId = responses.map((response) => response.regionId);
      })
    );
  }

  public updateRegionAssignment(): Observable<any> {
    return this.deleteOldRegionId().pipe(switchMap(() => this.postMunicipalityRegionMapping()));
  }

  private deleteTestData(): Observable<any> {
    return this._testDataService.delete(this._userMunicipalitiesService.currentMunicipality.key).pipe(
      switchMap(() => {
        this._configStore.setMunicipalityConfigState(this._configStore.municipalityStoreConfig);
        return this._municipalityManagementDialogHelperService.testDataSuccess();
      }),
      catchError(() => this._municipalityManagementDialogHelperService.testDataErrorGeneral())
    );
  }
}
